/**
 * Retreat Checkout Page
 * 
 * Embeds the Ontraport payment form using an iframe approach.
 * The backend serves the HTML with the Ontraport script properly embedded.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Lock, Shield, CheckCircle, Sparkles, Loader2 } from 'lucide-react';
import { getAffiliateCode } from '@/hooks/useAffiliateTracking';
import axios from 'axios';

const API_URL = window.location.origin;

const ROOM_NAMES = {
  'geodesic-dome': 'Sacred Geodesic Domes',
  'bungalow-double': 'Exotic Jungle Bungalows (Double)',
  'bungalow-single': 'Exotic Jungle Bungalows (Single)',
  'luxury-villa': 'Luxurious Private Villas'
};

const PAYMENT_LABELS = {
  'fullPay': 'Full Payment',
  'paymentPlan2': '2-Payment Plan',
  'paymentPlan3': '3-Payment Plan',
  'fullPayVip': 'Full Payment + VIP',
  'paymentPlan2Vip': '2-Payment Plan + VIP',
  'paymentPlan3Vip': '3-Payment Plan + VIP'
};

const RetreatCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  
  const roomId = searchParams.get('room');
  const planKey = searchParams.get('plan');
  const total = searchParams.get('total');
  
  const formKey = roomId && planKey ? `${roomId}-${planKey}` : null;
  const roomName = roomId ? ROOM_NAMES[roomId] : 'Retreat';
  const paymentLabel = planKey ? PAYMENT_LABELS[planKey] : '';
  const isVip = planKey?.includes('Vip');

  // Attribute affiliate code when checkout page loads
  useEffect(() => {
    const refCode = getAffiliateCode();
    if (refCode) {
      // Listen for the Ontraport form to capture email, or just store the code
      // The webhook will check affiliate_attributions collection
      axios.post(`${API_URL}/api/affiliates/attribute`, {
        affiliate_code: refCode,
        email: searchParams.get('email') || ''
      }).catch(() => {});
    }
  }, [searchParams]);

  // Build the iframe URL
  const iframeUrl = formKey ? `${API_URL}/api/retreat/checkout-form/${formKey}` : null;

  if (!formKey) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <p className="text-red-600 mb-4">Invalid checkout configuration. Please go back and try again.</p>
            <Link to="/retreat-booking">
              <Button className="btn-primary">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return to Booking
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-forest/5 to-cream">
      {/* Header */}
      <div className="bg-forest text-white py-4">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/retreat-booking" className="inline-flex items-center text-white/80 hover:text-white mb-2 text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Booking
          </Link>
          <h1 className="font-heading text-2xl">Complete Your Reservation</h1>
          <p className="text-white/70 text-sm mt-1">Just Be: Soft Power™ Retreat • June 26 - July 1, 2026</p>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Order Summary Bar */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-forest">{roomName}</h2>
                  <p className="text-sm text-gray-600">{paymentLabel}</p>
                </div>
                {isVip && (
                  <span className="inline-flex items-center gap-1 bg-terracotta/10 text-terracotta px-2 py-1 rounded text-sm">
                    <Sparkles className="h-3 w-3" />
                    VIP
                  </span>
                )}
              </div>
              {total && (
                <div className="text-right">
                  <p className="text-2xl font-bold text-forest">${parseInt(total).toLocaleString()}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Security Badges */}
        <div className="flex items-center justify-center gap-6 mb-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            <span>Secure Checkout</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span>SSL Encrypted</span>
          </div>
        </div>
        
        {/* Payment Form Container */}
        <Card className="overflow-hidden">
          <CardContent className="p-0 relative">
            {/* Loading indicator */}
            {!iframeLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 min-h-[600px]">
                <Loader2 className="h-12 w-12 animate-spin text-terracotta mb-4" />
                <p className="text-gray-700 font-medium">Loading secure payment form...</p>
                <p className="text-sm text-gray-500 mt-2">Please wait while we connect to our payment processor</p>
              </div>
            )}
            
            {/* Ontraport form iframe */}
            <iframe
              src={iframeUrl}
              title="Payment Form"
              className="w-full border-0"
              style={{ minHeight: '800px', height: '100%' }}
              onLoad={() => setIframeLoaded(true)}
              data-testid="ontraport-iframe"
            />
          </CardContent>
        </Card>
        
        {/* Help Text */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Having trouble? Contact us at{' '}
          <a href="mailto:info@volitionenterprises.com" className="text-terracotta hover:underline">
            info@volitionenterprises.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default RetreatCheckoutPage;
